import { useState, useEffect, useCallback } from "react";
import {
  passportReaderService,
  PassportReaderStatus,
  PassportReaderEvent,
  PassportData,
  PassportReaderMessage,
} from "../services/passportReader/passportReaderService";

export interface UsePassportReaderReturn {
  status: PassportReaderStatus;
  isConnected: boolean;
  isReady: boolean;
  error: string | null;
  passportData: PassportData | null;
  connect: () => void;
  disconnect: () => void;
  readPassport: () => void;
  clearError: () => void;
  clearPassportData: () => void;
}

export const usePassportReader = (): UsePassportReaderReturn => {
  const [status, setStatus] = useState<PassportReaderStatus>(
    PassportReaderStatus.DISCONNECTED,
  );
  const [error, setError] = useState<string | null>(null);
  const [passportData, setPassportData] = useState<PassportData | null>(null);

  useEffect(() => {
    const unsubscribe = passportReaderService.addEventListener(
      (event: PassportReaderEvent) => {
        setStatus(event.status);

        switch (event.type) {
          case PassportReaderMessage.INIT_FAILURE:
          case PassportReaderMessage.READ_ERROR:
          case PassportReaderMessage.DEVICE_DISCONNECTED:
            setError(event.error || "An error occurred");
            break;

          case PassportReaderMessage.READ_SUCCESS:
            if (event.data) {
              setPassportData(event.data);
              setError(null);
            }
            break;

          case PassportReaderMessage.INIT_SUCCESS:
          case PassportReaderMessage.DEVICE_CONNECTED:
            setError(null);
            break;

          default:
            break;
        }
      },
    );

    return () => {
      unsubscribe();
    };
  }, []);

  const connect = useCallback(() => {
    setError(null);
    passportReaderService.connect();
  }, []);

  const disconnect = useCallback(() => {
    setError(null);
    setPassportData(null);
    passportReaderService.disconnect();
  }, []);

  const readPassport = useCallback(() => {
    setError(null);
    passportReaderService.readPassport();
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearPassportData = useCallback(() => {
    setPassportData(null);
  }, []);

  return {
    status,
    isConnected: passportReaderService.isConnected(),
    isReady: passportReaderService.isReady(),
    error,
    passportData,
    connect,
    disconnect,
    readPassport,
    clearError,
    clearPassportData,
  };
};
